import type { ChangelogActionRow, ChangelogItemRow } from '@entities/changelog'

import { useChangelogStore } from '@entities/changelog'
import { resolveUrl } from '@shared/lib/url'
import { useAdminRoutes } from '@shared/lib/routes'
import { AppRoute } from '@shared/config'

import { useTableChangelog } from './table-changelog.hook'

type TableChangelogReturn = ReturnType<typeof useTableChangelog>

interface TableEntityChangelogReturn extends TableChangelogReturn {
  getUserLabel: (row: ChangelogActionRow) => string
  getUserLink: (row: ChangelogActionRow) => string | null
  getItemInstance: (item: ChangelogItemRow) => string
  openItemDetail: (item: ChangelogItemRow) => void
}

const USER_DISPLAY_TYPE = {
  Admin: 'Admin',
  Tutor: 'Tutor',
  Customer: 'Customer',
} as const

/** The legacy entity-changelog table: the shared changelog table logic plus the actor link and the item detail. */
export const useTableEntityChangelog = (): TableEntityChangelogReturn => {
  const store = useChangelogStore()
  const table = useTableChangelog()

  const getUserLabel = (row: ChangelogActionRow): string =>
    row.user_display_type ? `${row.user_display_type} #${row.user_id} (${row.user_name})` : ''

  const getUserLink = (row: ChangelogActionRow): string | null => {
    if (!row.user_id) return null

    switch (row.user_display_type) {
      case USER_DISPLAY_TYPE.Admin:
        return resolveUrl(AppRoute.UserEdit, { id: row.user_id })
      case USER_DISPLAY_TYPE.Tutor:
        return useAdminRoutes('admin.tutor.show', { tutor_id: row.user_id })
      case USER_DISPLAY_TYPE.Customer:
        return resolveUrl(AppRoute.CustomersShow, { id: row.user_id })
      default:
        return null
    }
  }

  const getItemInstance = (item: ChangelogItemRow): string => `${item.instance_type} #${item.instance_id}`

  const openItemDetail = (item: ChangelogItemRow): void => {
    store.setDetailItem(item)
  }

  return {
    ...table,
    getUserLabel,
    getUserLink,
    getItemInstance,
    openItemDetail,
  }
}
